import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Admin logbook management endpoints (diet hub -> per-diet list -> review
 * detail). Mirrors the admin upgrade-applications API. Backend routes:
 *   GET /admin/logbooks/diets   — diets with logbook counts (hub)
 *   GET /admin/logbooks         — paginated logbooks, filter by diet/status/stage
 *   GET /admin/logbooks/{id}    — one logbook with entries/report/assessment
 */
export type AdminLogbooksArgs = {
  logbook_diet_id?: string;
  status?: string;
  stage?: number | string;
  page?: number;
  per_page?: number;
  search?: string;
};

export const adminLogbookApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    adminLogbookDiets: builder.query<any[], void>({
      query: () => "/admin/logbooks/diets",
      transformResponse: (res: any) => res?.data ?? res ?? [],
    }),

    adminLogbooks: builder.query<any, AdminLogbooksArgs | void>({
      query: (arg) => {
        const p = new URLSearchParams();
        if (arg?.logbook_diet_id) p.set("logbook_diet_id", String(arg.logbook_diet_id));
        if (arg?.status && arg.status !== "all") p.set("status", String(arg.status));
        if (arg?.stage) p.set("stage", String(arg.stage));
        if (arg?.page) p.set("page", String(arg.page));
        if (arg?.per_page) p.set("per_page", String(arg.per_page));
        if (arg?.search) p.set("search", String(arg.search));
        const qs = p.toString();
        return `/admin/logbooks${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Logbook"],
    }),

    // Server-side status counts for one diet (across ALL its logbooks, not a page).
    adminDietSummary: builder.query<any, string>({
      query: (dietId) => `/admin/logbooks/diets/${dietId}/summary`,
      transformResponse: (res: any) => res?.data ?? res ?? {},
      providesTags: ["Logbook"],
    }),

    adminLogbookDetail: builder.query<any, string>({
      query: (id) => `/admin/logbooks/${id}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: ["Logbook"],
    }),

    // Member typeahead for the admin "create logbook" form.
    adminMemberLookup: builder.query<any[], string>({
      query: (q) => `/admin/logbooks/member-lookup?q=${encodeURIComponent(q)}`,
      transformResponse: (res: any) => res?.data ?? res ?? [],
    }),

    // Create a logbook for a member (privilege: application_diet.create).
    adminCreateLogbook: builder.mutation<
      any,
      {
        user_id: string;
        logbook_diet_id: string;
        stage: number;
        status?: string;
        supervisor_id?: string;
        total_score?: number | null;
        final_grade?: string | null;
        remarks?: string | null;
      }
    >({
      query: (body) => ({ url: "/admin/logbooks", method: "POST", body }),
      invalidatesTags: ["Logbook"],
    }),

    // Override a logbook's status (privilege: application_diet.activate).
    // POST, not PATCH — the host blocks PATCH.
    adminUpdateLogbookStatus: builder.mutation<any, { id: string; status: string }>({
      query: ({ id, status }) => ({ url: `/admin/logbooks/${id}/status`, method: "POST", body: { status } }),
      invalidatesTags: ["Logbook"],
    }),
  }),
});

export const {
  useAdminLogbookDietsQuery,
  useAdminLogbooksQuery,
  useAdminDietSummaryQuery,
  useAdminLogbookDetailQuery,
  useLazyAdminMemberLookupQuery,
  useAdminCreateLogbookMutation,
  useAdminUpdateLogbookStatusMutation,
} = adminLogbookApi;
